import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  Mail,
  Plus,
  Trash2,
  Play,
  Pause,
  Send,
  RefreshCw,
  Calendar,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  X
} from 'lucide-react';

const ScheduledReports = () => {
  const [reports, setReports] = useState([]);
  const [reportTypes, setReportTypes] = useState({});
  const [frequencies, setFrequencies] = useState({});
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    report_type: '',
    frequency: 'weekly',
    recipients: [],
    include_charts: true,
    include_details: true
  });
  
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reportsRes, typesRes] = await Promise.all([
        api.get('/scheduled-reports'),
        api.get('/scheduled-reports/types')
      ]);
      
      setReports(reportsRes.data || []);
      setReportTypes(typesRes.data?.types || {});
      setFrequencies(typesRes.data?.frequencies || {});
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Email inválido');
      return;
    }
    if (form.recipients.includes(newEmail)) {
      toast.error('Email ya agregado');
      return;
    }
    setForm(prev => ({
      ...prev,
      recipients: [...prev.recipients, newEmail]
    }));
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setForm(prev => ({
      ...prev,
      recipients: prev.recipients.filter(e => e !== email)
    }));
  };

  const handleCreate = async () => {
    if (!form.name || !form.report_type || form.recipients.length === 0) {
      toast.error('Completa todos los campos requeridos');
      return;
    }
    
    try {
      const res = await api.post('/scheduled-reports', form);
      setReports(prev => [res.data, ...prev]);
      setIsDialogOpen(false);
      resetForm();
      toast.success('Reporte programado creado');
    } catch (error) {
      toast.error('Error al crear reporte');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este reporte programado?')) return;
    
    try {
      await api.delete(`/scheduled-reports/${id}`);
      setReports(prev => prev.filter(r => r.id !== id));
      toast.success('Reporte eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleToggle = async (report) => {
    try {
      const res = await api.post(`/scheduled-reports/${report.id}/toggle`);
      setReports(prev => prev.map(r => r.id === report.id ? res.data : r));
      toast.success(res.data.active ? 'Reporte activado' : 'Reporte pausado');
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleSendNow = async (report) => {
    try {
      toast.info(`Enviando ${report.name}...`);
      const res = await api.post(`/scheduled-reports/${report.id}/send-now`);
      
      if (res.data.success) {
        toast.success(res.data.message || 'Reporte enviado');
      } else {
        toast.error(res.data.message || 'Error al enviar');
      }
      
      loadData();
    } catch (error) {
      toast.error('Error al enviar reporte');
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      report_type: '',
      frequency: 'weekly',
      recipients: [],
      include_charts: true,
      include_details: true
    });
    setNewEmail('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6 p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="text-indigo-600" size={24} />
            Reportes Programados
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Configura el envío automático de reportes por email
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: '#4f46e5' }}>
                <Plus size={16} className="mr-2" />
                Nuevo Reporte
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nuevo Reporte Programado</DialogTitle>
                <DialogDescription>
                  Configura un reporte automático por email
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label>Nombre del reporte</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ej: Resumen Semanal de Alertas"
                    className="mt-1"
                    style={{ backgroundColor: '#fff' }}
                  />
                </div>
                
                <div>
                  <Label>Tipo de reporte</Label>
                  <Select
                    value={form.report_type}
                    onValueChange={(v) => setForm(prev => ({ ...prev, report_type: v }))}
                  >
                    <SelectTrigger className="mt-1" style={{ backgroundColor: '#fff' }}>
                      <SelectValue placeholder="Seleccionar tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(reportTypes).map(([key, info]) => (
                        <SelectItem key={key} value={key}>
                          {info.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.report_type && reportTypes[form.report_type] && (
                    <p className="text-xs text-slate-400 mt-1">
                      {reportTypes[form.report_type].description}
                    </p>
                  )}
                </div>
                
                <div>
                  <Label>Frecuencia</Label>
                  <Select
                    value={form.frequency}
                    onValueChange={(v) => setForm(prev => ({ ...prev, frequency: v }))}
                  >
                    <SelectTrigger className="mt-1" style={{ backgroundColor: '#fff' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(frequencies).map(([key, info]) => (
                        <SelectItem key={key} value={key}>
                          {info.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="mb-2 block">Destinatarios</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {form.recipients.map(email => (
                      <Badge
                        key={email}
                        variant="outline"
                        className="bg-blue-50 text-blue-700 flex items-center gap-1 pr-1"
                      >
                        <Mail size={12} />
                        {email}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 hover:bg-red-100 rounded-full"
                          onClick={() => handleRemoveEmail(email)}
                        >
                          <X size={10} />
                        </Button>
                      </Badge>
                    ))}
                    {form.recipients.length === 0 && (
                      <span className="text-sm text-slate-400">Sin destinatarios</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="email@ejemplo.com"
                      className="flex-1"
                      style={{ backgroundColor: '#fff' }}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                    />
                    <Button variant="outline" size="sm" onClick={handleAddEmail}>
                      <Plus size={14} />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-3 pt-2">
                  <Label className="text-sm font-medium">Opciones</Label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-slate-600">Incluir gráficos</span>
                    <Switch
                      checked={form.include_charts}
                      onCheckedChange={(v) => setForm(prev => ({ ...prev, include_charts: v }))}
                    />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-slate-600">Incluir detalles</span>
                    <Switch
                      checked={form.include_details}
                      onCheckedChange={(v) => setForm(prev => ({ ...prev, include_details: v }))}
                    />
                  </label>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreate}
                  style={{ backgroundColor: '#4f46e5' }}
                  disabled={!form.name || !form.report_type || form.recipients.length === 0}
                >
                  Crear
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Lista de Reportes */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="animate-spin text-slate-400" size={24} />
        </div>
      ) : reports.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <BarChart3 size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No hay reportes programados</p>
            <p className="text-sm text-slate-400 mt-1">
              Crea un reporte para recibir información automáticamente por email
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {reports.map(report => (
            <Card
              key={report.id}
              className={`bg-white transition-all ${!report.active ? 'opacity-60' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <FileText size={18} className="text-indigo-600" />
                      <h4 className="font-medium text-slate-900">{report.name}</h4>
                      <Badge
                        variant="outline"
                        className={report.active 
                          ? 'bg-green-50 text-green-700 border-green-200' 
                          : 'bg-slate-50 text-slate-500'}
                      >
                        {report.active ? 'Activo' : 'Pausado'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      {report.report_type_info?.name || report.report_type}
                    </p>
                  </div>
                  <Switch
                    checked={report.active}
                    onCheckedChange={() => handleToggle(report)}
                  />
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge variant="outline" className="text-xs flex items-center gap-1">
                    <Clock size={10} />
                    {report.frequency_info?.label || report.frequency}
                  </Badge>
                  <Badge variant="outline" className="text-xs flex items-center gap-1">
                    <Users size={10} />
                    {report.recipients?.length || 0} destinatarios
                  </Badge>
                  <Badge variant="outline" className="text-xs flex items-center gap-1">
                    <Send size={10} />
                    {report.send_count || 0} envíos
                  </Badge>
                </div>

                {report.last_sent && (
                  <div className="text-xs text-slate-400 mb-3">
                    Último envío: {formatDate(report.last_sent)}
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <div className="flex flex-wrap gap-1">
                    {report.recipients?.slice(0, 2).map(email => (
                      <Badge key={email} variant="outline" className="text-xs bg-slate-50">
                        {email}
                      </Badge>
                    ))}
                    {report.recipients?.length > 2 && (
                      <Badge variant="outline" className="text-xs bg-slate-50">
                        +{report.recipients.length - 2}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSendNow(report)}
                      title="Enviar ahora"
                      className="text-indigo-600"
                    >
                      <Send size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(report.id)}
                      className="text-red-500 hover:text-red-600"
                      title="Eliminar"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScheduledReports;
